import { generateSaveDispatches, getPersonsFromIds } from '../../shared/utils/movePersonsUtils'
import { restoreRelationshipsAmongMovedPersons } from '../../shared/relationships/restoreRelationships'
import { superDeduper } from '../../../../views/indexing/components/editPanels/utils/editPanelDispatchBuilders'

/**
 * Builds the dispatches that gather people into a brand new group.
 *
 * Primary is settled here rather than inherited: a move leaves each person's `primary` flag
 * untouched, so somebody who led the group they are leaving would otherwise arrive still flagged.
 *
 * @param {Object} params
 * @param {Array<string>} params.personIds - Everyone the patron chose to move into the new group.
 * @param {Array<string>} [params.primaryPersonIds] - Who should be primary in the new group. A lone
 *   mover need not appear here; they are made primary either way.
 * @param {Array<Object>} params.images - SLS images from state.
 * @param {string} params.currentPartitionKey - Partition the new group belongs to.
 * @returns {Promise<Array<Object>>} SLS dispatches.
 */
export const getNewGroupDispatches = async ({ personIds = [], primaryPersonIds = [], images, currentPartitionKey }) => {
  const personsData = getPersonsFromIds({ images, personsIds: personIds })

  if (personsData.length === 0) return []

  // Both ends of a relationship hold the same element, so moving a pair emits its delete twice.
  const dispatches = superDeduper(
    await generateSaveDispatches({
      recordIdParam: 'new_record',
      personsData,
      currentPartitionKey,
      images,
      // No record-type picker on this entry point, so the group is created untyped.
      recordType: '',
    })
  )

  const restoreDispatches = restoreRelationshipsAmongMovedPersons({
    movedPersons: personsData,
    partitionKey: currentPartitionKey,
  })

  const movedPersonIds = new Set(personsData.map((person) => person.id))
  const shouldBePrimary = new Set(primaryPersonIds.filter((personId) => movedPersonIds.has(personId)))
  // A group of one always has a primary, which is why the flow never asks a lone mover.
  if (personsData.length === 1) shouldBePrimary.add(personsData[0].id)

  const primaryDispatches = personsData
    .filter((person) => Boolean(person.primary) !== shouldBePrimary.has(person.id))
    .map((person) => ({
      type: 'SET_PRIMARY',
      value: {
        partitionKey: person.partitionKey || currentPartitionKey,
        id: person.id,
        primary: shouldBePrimary.has(person.id),
      },
    }))

  const isRecordPersonIdsStamp = (dispatch) => dispatch.type === 'SET_RECORD_PERSON_IDS'

  return [
    ...dispatches.filter((dispatch) => !isRecordPersonIdsStamp(dispatch)),
    ...restoreDispatches,
    ...primaryDispatches,
    ...dispatches.filter(isRecordPersonIdsStamp),
  ]
}
